import React, { useEffect, useState } from 'react';
import { isEmpty, template } from 'lodash';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Button, SECONDARY, TERTIARY } from '@glu/buttons-react';
import { Dropdown } from '@glu/dropdown-react';
import {
  EnvelopeNextIcon,
  TriangleNextIcon,
  PlusCircleIcon,
  CloseXAshIcon
} from '@glu/icons-react';

import Card from 'components/card/Card';
import Checkbox from 'components/checkbox/Checkbox';
import Input from 'components/gluValidation/Input';
import {
  FIELD_NAMES,
  CONTACT_POINTS,
  SUBMIT_TYPES
} from 'components/securityContact/securityContacts.constants';
import useLocale from 'components/useLocale/useLocale';
import AppPropTypes from 'proptypes/AppPropTypes';
import { regexp } from 'utils/validations/gluValidations';
import { MATCHERS, TEXT_MASKS } from 'constants.js';

const getDelayOptions = (locale) =>
  Array.from({ length: 9 }, (_, i) => ({
    id: 2 * (i + 1),
    name: template(locale['common.time.template.seconds'])({ seconds: 2 * (i + 1) })
  }));

const FirstTimeSecurityContacts = ({
  classes,
  contactPoint,
  onChange,
  onSubmit,
  setContactPoint,
  user,
  validate,
  values
}) => {
  const locale = useLocale();
  const [delayOptions] = useState(getDelayOptions(locale));
  const securityValues = values.security || {};

  useEffect(() => {
    let changes = {};
    if (securityValues.includeEmail === undefined) {
      changes = {
        includeEmail: !user.isEmailOTPDisallowed
      };
    }
    if (
      user.isEmailOTPDisallowed === true &&
      !securityValues.mobileContact &&
      !securityValues.voiceContact &&
      !securityValues.smsNumber
    ) {
      changes = {
        ...changes,
        mobileContact: true,
        smsNumber: user.userInformation?.phoneNumber,
        ...(user?.voiceEnabled ? { voiceNumber: user.userInformation?.phoneNumber } : {})
      };
    }
    if (Object.keys(changes).length) {
      onChange(FIELD_NAMES.SECURITY, { ...values.security, ...changes });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={classes.wrapper}>
      <h2 className={classes.boldText}>{locale['security.securityContactInformation']}</h2>
      <p className={classes.verticalGap}>{locale['security.securityContactText']}</p>
      <h3 className={classes.boldText}>{locale['security.contactDetail']}</h3>
      <p className={classes.errorText}>{contactPoint.error || ''}</p>

      {!user.isEmailOTPDisallowed && (
        <>
          {/* Allow user to include or not include email as contact point, as long as there is a default contact */}
          {securityValues.includeEmail ? (
            <Card
              data-qa="email-card"
              className={classnames(classes.card, classes.contactDetail, classes.verticalGap)}
            >
              {contactPoint.contactPointReadyToBeAdded && securityValues.makeDefault && (
                <CloseXAshIcon
                  className={classes.closeIcon}
                  onClick={() =>
                    onChange(FIELD_NAMES.SECURITY, { ...securityValues, includeEmail: false })
                  }
                />
              )}
              <div className={classes.emailIconContainer}>
                <EnvelopeNextIcon
                  className={classes.emailIcon}
                  textVisible
                  text={`${locale['common.email']}`}
                />
              </div>
              <div className={classes.flexpand}>
                <small>{locale['common.emailAddress']}</small>
                <br />
                {user.userInformation?.email || locale['security.contact.email.notFound']}
              </div>
              {/* If user did not select sms as default, then show flag here */}
              {!securityValues.makeDefault && !securityValues.makeVoiceDefault && (
                <p className={classes.defaultIndicator}>{locale['common.default']}</p>
              )}
            </Card>
          ) : (
            <Button
              className={classnames('btn btn-tertiary', classes.verticalGap)}
              onClick={() =>
                onChange(FIELD_NAMES.SECURITY, { ...securityValues, includeEmail: true })
              }
              variant={TERTIARY}
              text={locale['security.contact.email.include']}
            />
          )}
        </>
      )}

      {!securityValues.mobileContact ? (
        <>
          <div
            data-qa="sms-security-warning"
            className={classnames(classes.extendedSecurityWarning, classes.verticalGap)}
          >
            <TriangleNextIcon className={classes.triangleIcon} />
            <p className={classes.zeroMargin}>{locale['security.extendedSecurityWarning']}</p>
          </div>
          <Button
            className={classnames(
              'btn btn-tertiary',
              classes.addContactButton,
              classes.verticalGap
            )}
            dataQa="add-sms-option"
            variant={TERTIARY}
            onClick={() => {
              onChange(FIELD_NAMES.SECURITY, {
                ...securityValues,
                mobileContact: true,
                smsNumber: user.userInformation?.phoneNumber,
                contactPoint: [CONTACT_POINTS.SMS]
              });
            }}
          >
            <PlusCircleIcon className={classes.circlePlusIcon} />
            <p className={classes.zeroMargin}>{locale['security.addMobileNumber']}</p>
          </Button>
        </>
      ) : (
        <>
          <Card
            data-qa="sms-card"
            className={classnames(classes.card, classes.contactPoint, classes.verticalGap)}
          >
            {/* Activation code was sent and is ready to be used when updatingUserContactInfo */}
            {contactPoint.contactPointReadyToBeAdded ? (
              <div className={classes.contactPointFields}>
                <div>
                  <p>{locale['security.contact.sms.verified']}</p>
                  <p>{securityValues.smsNumber}</p>
                </div>
                <label htmlFor="mobileAsDefault" className={classes.makeDefaultLabel}>
                  <Checkbox
                    checked={securityValues.makeDefault}
                    onChange={(e) =>
                      onChange(FIELD_NAMES.SECURITY, {
                        ...securityValues,
                        makeDefault: e.target.checked,
                        makeVoiceDefault: false
                      })
                    }
                    name="mobileAsDefault"
                    id="mobileAsDefault"
                    data-qa="mobileAsDefault"
                  />
                  <span className={classnames(classes.makeDefaultText, classes.readyForDefault)}>
                    {locale['common.makeDefault']}
                  </span>
                </label>
                <CloseXAshIcon
                  className={classes.closeIcon}
                  onClick={() => onSubmit(SUBMIT_TYPES.REMOVE_MOBILE_CONTACT)}
                />
              </div>
            ) : (
              // Process for sending and verifying activation code
              <>
                {contactPoint.verifyingCode ? (
                  <>
                    {contactPoint.verifyingCodeError && (
                      <p className={classes.invalidCode}>
                        <small>Invalid Code</small>
                      </p>
                    )}
                    <div className={classes.contactPointFields}>
                      <Input
                        name="activationCode"
                        className={classnames(
                          classes.fullWidth,
                          'form-group',
                          classes.marginRight
                        )}
                        data-qa="activationCode"
                        defaultValue={securityValues.enterCode}
                        label={locale['security.contact.sms.enterCode']}
                        validators={[{ required: true, msg: locale['validation.required'] }]}
                      />
                      {/* Margin top is the height of the label on input field */}
                      <small className={classes.enterCodeText}>
                        {locale['security.contact.sms.enterCodeText']}
                      </small>
                    </div>
                    <div className={classes.formSectionBtnContainer}>
                      <Button
                        className="btn btn-secondary"
                        variant={SECONDARY}
                        disabled={contactPoint.checkingCode}
                        onClick={async () => {
                          const { errors } = await validate();
                          const hasError = errors?.security?.activationCode ?? false;
                          if (isEmpty(hasError)) onSubmit(SUBMIT_TYPES.VERIFY_CODE);
                        }}
                      >
                        {locale[contactPoint.checkingCode ? 'common.checking' : 'common.submit']}
                      </Button>
                      <Button
                        className="btn btn-tertiary"
                        variant={TERTIARY}
                        disabled={contactPoint.sendingCode}
                        onClick={() => onSubmit(SUBMIT_TYPES.SEND_CODE)}
                      >
                        {
                          locale[
                            contactPoint.sendingCode
                              ? 'security.contact.sms.sendingCode'
                              : 'security.contact.sms.resendCode'
                          ]
                        }
                      </Button>
                      <span>|</span>
                      <Button
                        className="btn btn-tertiary"
                        variant={TERTIARY}
                        onClick={() =>
                          setContactPoint((state) => ({ ...state, verifyingCode: false }))
                        }
                      >
                        {locale['common.cancel']}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={classes.contactPointFields}>
                      <Input
                        name="smsNumber"
                        className={classnames(
                          classes.fullWidth,
                          'form-group',
                          classes.marginRight,
                          classes.alignInput
                        )}
                        data-qa="smsNumber"
                        defaultValue={securityValues.smsNumber}
                        label={locale['common.smsNumber']}
                        mask={TEXT_MASKS.PHONE_NUMBER}
                        readOnly={!!user.isEmailOTPDisallowed}
                        validators={[
                          { required: true, msg: locale['validation.required'] },
                          {
                            fn: (key, _param, data) => {
                              const smsNumber = data[key].replace(/\D/g, '');
                              return !smsNumber.match(/^[0,1]/);
                            },
                            msg: locale['common.phoneNumber.error']
                          },
                          {
                            us10DigitPhone: true,
                            msg: locale['common.phoneNumber.error']
                          }
                        ]}
                      />
                      <label htmlFor="makeDefault" className={classes.makeDefaultLabel}>
                        <Checkbox
                          checked={securityValues.makeDefault}
                          onChange={(e) =>
                            onChange(FIELD_NAMES.SECURITY, {
                              ...securityValues,
                              makeDefault: e.target.checked,
                              makeVoiceDefault: false
                            })
                          }
                          name="makeDefault"
                          id="makeDefault"
                          data-qa="makeDefault"
                        />
                        <span className={classes.makeDefaultText}>
                          {locale['common.makeDefault']}
                        </span>
                      </label>
                    </div>
                    <div className={classes.formSectionBtnContainer}>
                      <Button
                        className="btn btn-secondary"
                        variant={SECONDARY}
                        disabled={contactPoint.sendingCode}
                        onClick={async () => {
                          const { errors } = await validate();
                          const hasError = errors?.security?.smsNumber ?? false;
                          if (isEmpty(hasError)) onSubmit(SUBMIT_TYPES.SEND_CODE);
                        }}
                      >
                        {
                          locale[
                            contactPoint.sendingCode
                              ? 'security.contact.sms.sendingCode'
                              : 'common.verify'
                          ]
                        }
                      </Button>
                      <Button
                        className="btn btn-tertiary"
                        variant={TERTIARY}
                        // TODO: Cancel sendActivationCode request
                        onClick={() =>
                          onChange(FIELD_NAMES.SECURITY, {
                            ...securityValues,
                            mobileContact: false,
                            makeDefault: false
                          })
                        }
                      >
                        {locale['common.cancel']}
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </Card>
        </>
      )}

      {!securityValues.voiceContact && user?.voiceEnabled && (
        <>
          <div
            data-qa="voice-security-warning"
            className={classnames(classes.extendedSecurityWarning, classes.verticalGap)}
          >
            <TriangleNextIcon className={classes.triangleIcon} />
            <p className={classes.zeroMargin}>{locale['security.extendedVoiceSecurityWarning']}</p>
          </div>
          <Button
            className={classnames(
              'btn btn-tertiary',
              classes.addContactButton,
              classes.verticalGap
            )}
            dataQa="add-voice-option"
            variant={TERTIARY}
            onClick={() => {
              onChange(FIELD_NAMES.SECURITY, {
                ...securityValues,
                voiceContact: true,
                voiceNumber: user.userInformation?.phoneNumber,
                contactPoint: [CONTACT_POINTS.VOICE]
              });
            }}
          >
            <PlusCircleIcon className={classes.circlePlusIcon} />
            <p className={classes.zeroMargin}>{locale['security.addVoiceNumber']}</p>
          </Button>
        </>
      )}
      {securityValues.voiceContact && user?.voiceEnabled && (
        <Card data-qa="voice-card" className={classnames(classes.card, classes.contactPoint)}>
          {/* Activation code was sent and is ready to be used when updatingUserContactInfo */}
          {/* {contactPoint.contactPointReadyToBeAdded ? ( */}
          <div className={classes.contactPointFields}>
            <div>
              <Input
                name="voiceNumber"
                className={classnames(
                  classes.fullWidth,
                  'form-group',
                  classes.marginRight,
                  classes.alignInput
                )}
                data-qa="voiceNumber"
                label={locale['common.voiceNumber']}
                readOnly={!!user.isEmailOTPDisallowed}
                validators={[
                  { required: true, msg: locale['validation.required'] },
                  regexp({
                    regexp: MATCHERS.PHONE,
                    msg: locale['common.phoneNumber.error']
                  })
                ]}
              />
              <Input
                name="extension"
                className={classnames(
                  classes.fullWidth,
                  'form-group',
                  classes.marginRight,
                  classes.alignInput,
                  classes.voiceExtension
                )}
                data-qa="extension"
                label={locale['common.extension']}
                optional
              />
              {securityValues.extension ? (
                <Dropdown
                  name="vruDelay"
                  className={classnames(
                    classes.fullWidth,
                    'form-group',
                    classes.alignDropdown,
                    classes.marginRight
                  )}
                  dataQa="vruDelay"
                  defaultValue={[delayOptions[0]]}
                  disabled={!securityValues.extension}
                  filterEnabled={false}
                  labelText={locale['common.delay']}
                  options={delayOptions}
                  optional
                />
              ) : null}
            </div>
            <label htmlFor="voiceAsDefault" className={classes.makeDefaultLabel}>
              <Checkbox
                checked={securityValues.makeVoiceDefault}
                onChange={(e) =>
                  onChange(FIELD_NAMES.SECURITY, {
                    ...securityValues,
                    makeDefault: false,
                    makeVoiceDefault: e.target.checked
                  })
                }
                name="voiceAsDefault"
                id="voiceAsDefault"
                data-qa="voiceAsDefault"
              />
              <span className={classnames(classes.makeDefaultText, classes.readyForDefault)}>
                {locale['common.makeDefault']}
              </span>
            </label>
          </div>
          <div className={classes.formSectionBtnContainer}>
            <Button
              className="btn btn-tertiary"
              variant={TERTIARY}
              // TODO: Cancel sendActivationCode request
              onClick={() =>
                onChange(FIELD_NAMES.SECURITY, {
                  ...securityValues,
                  voiceContact: false,
                  makeVoiceDefault: false,
                  voiceNumber: '',
                  extension: '',
                  vruDelay: delayOptions[0]
                })
              }
            >
              {locale['common.cancel']}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

FirstTimeSecurityContacts.propTypes = {
  classes: AppPropTypes.jssClasses.isRequired,
  contactPoint: PropTypes.shape({}).isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  setContactPoint: PropTypes.func.isRequired,
  user: PropTypes.shape({
    isEmailOTPDisallowed: PropTypes.bool,
    voiceEnabled: PropTypes.bool,
    userInformation: PropTypes.shape({
      email: PropTypes.string,
      phoneNumber: PropTypes.string
    }).isRequired
  }).isRequired,
  validate: PropTypes.func.isRequired,
  values: PropTypes.shape({}).isRequired
};

export default FirstTimeSecurityContacts;
